import { skipToken, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { reportingStoreQueryKeys } from '@/common/api/reporting-store/queryKeys';
import type { ItemHealth, ItemHealthByItemIdsResponse } from '@/common/api/types/dashboard/health';
import { HealthApi } from '@/features/insights-dashboard/api/health';
import { INSIGHTS_STALE_TIME } from '@/features/insights-dashboard/components/data/constants';

const ITEM_HEALTH_CHUNK_SIZE = 200;

const chunkArray = <T>(arr: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

export const useItemHealth = ({
  itemIds,
  enabled = true,
}: {
  itemIds: number[];
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();

  const chunks = useMemo(() => chunkArray(itemIds, ITEM_HEALTH_CHUNK_SIZE), [itemIds]);

  return useQueries({
    queries: chunks.map((chunk) => ({
      queryKey: reportingStoreQueryKeys.health.byItemIds(chunk),
      queryFn: async () => {
        const response = await HealthApi.getItemHealth({ itemIds: chunk });
        response.data.forEach((itemHealth) => {
          queryClient.setQueryData(
            reportingStoreQueryKeys.health.byItemId(itemHealth.itemId),
            itemHealth,
          );
        });
        return response;
      },
      enabled,
      staleTime: INSIGHTS_STALE_TIME,
      placeholderData: (previousData: ItemHealthByItemIdsResponse | undefined) => previousData,
      retry: false,
    })),
    combine: (results) => ({
      isLoading: results.some((r) => r.isLoading),
      data: results.flatMap((r) => r.data?.data ?? []),
    }),
  });
};

export const useItemHealthById = ({
  itemId,
  enabled = true,
}: {
  itemId: number;
  enabled?: boolean;
}) => {
  return useQuery<ItemHealth>({
    queryKey: reportingStoreQueryKeys.health.byItemId(itemId),
    queryFn: skipToken,
    enabled,
    staleTime: INSIGHTS_STALE_TIME,
  });
};
